/**
 * @deprecated New code should use `useApiError` (from @/shared/hooks/useApiError)
 * which routes envelopes to the right surface (toast, inline, modal). The helpers
 * in this file remain only as a bridge for legacy call sites until the
 * controller/form sweep migrates them. Internally they route through
 * `normalizeError` so they emit the same userMessage as the new pipeline.
 *
 * IMPORTANT: each helper marks the envelope handled in the shared toast dedup
 * map. The semantics is "if a caller is extracting the message, they're going
 * to surface it themselves" — without this, the global MutationCache fallback
 * in queryClient.ts would emit a *second* toast on top of the legacy call
 * site's `notify.error(...)`. The mark ensures the global fallback dedups.
 */
import { normalizeError } from "@/shared/errors/normalizeError";
import { markEnvelopeHandled } from "@/shared/errors/routeErrorEnvelope";

export interface StandardApiError {
  message: string;
  errors?: Record<string, string[]>;
}

/**
 * @deprecated Use `useApiError(err, { formId })` for inline rendering, or read
 * `err.envelope.userMessage` directly from the axios error.
 */
export const normalizeApiError = (error: unknown): StandardApiError => {
  const env = normalizeError(error);
  markEnvelopeHandled(env);
  return {
    message: env.userMessage,
    errors: fieldErrorsToRecord(env.fieldErrors),
  };
};

/**
 * @deprecated Use `useFormFieldErrors(formId)` from FieldErrorContext.
 */
export const normalizeFieldErrors = (
  errors: Record<string, unknown> | string[]
): Record<string, string[]> => {
  const normalized: Record<string, string[]> = {};

  if (Array.isArray(errors)) {
    for (const error of errors) {
      if (typeof error !== "string") continue;
      const colonIdx = error.indexOf(":");
      if (colonIdx > 0) {
        const fieldName = toCamelCase(error.substring(0, colonIdx).trim());
        const message = error.substring(colonIdx + 1).trim();
        if (!normalized[fieldName]) normalized[fieldName] = [];
        normalized[fieldName].push(message);
      } else {
        if (!normalized["general"]) normalized["general"] = [];
        normalized["general"].push(error);
      }
    }
    return normalized;
  }

  for (const [field, value] of Object.entries(errors)) {
    const camelCaseField = toCamelCase(field);
    if (Array.isArray(value)) {
      normalized[camelCaseField] = value.map((v) => String(v));
    } else if (typeof value === "string") {
      normalized[camelCaseField] = [value];
    } else {
      normalized[camelCaseField] = [String(value)];
    }
  }
  return normalized;
};

/**
 * @deprecated Read `err.envelope.userMessage` from the axios error, or use `useApiError`.
 */
export const getErrorMessage = (error: unknown): string => {
  const env = normalizeError(error);
  markEnvelopeHandled(env);
  return env.userMessage;
};

/**
 * @deprecated Use `useFormFieldErrors(formId)` after `useApiError(err, { formId })`.
 */
export const getFieldErrors = (
  error: unknown
): Record<string, string[]> | undefined => {
  const env = normalizeError(error);
  markEnvelopeHandled(env);
  if (env.fieldErrors.length === 0) return undefined;
  return fieldErrorsToRecord(env.fieldErrors);
};

const toCamelCase = (str: string): string => {
  if (!str) return str;
  return str.charAt(0).toLowerCase() + str.slice(1);
};

const fieldErrorsToRecord = (
  fieldErrors: Array<{ field: string; userMessage: string }>
): Record<string, string[]> | undefined => {
  if (fieldErrors.length === 0) return undefined;
  const out: Record<string, string[]> = {};
  for (const fe of fieldErrors) {
    if (!out[fe.field]) out[fe.field] = [];
    out[fe.field].push(fe.userMessage);
  }
  return out;
};
